<script setup lang="ts">
import { Icon } from '@iconify/vue';
/**
 * UiPagination - Design System Pagination Component
 *
 * A pagination control for navigating through pages of content.
 * Matches the styling of the live platform pagination-bar.
 *
 * @example
 * <UiPagination
 *   :current-page="1"
 *   :total-pages="10"
 *   @page-change="handlePageChange"
 * />
 */
import { computed } from 'vue';
import { IconLib } from '@/assets/icon-library';

export interface UiPaginationProps {
   /** Current page number (1-indexed) */
   currentPage: number;
   /** Total number of pages */
   totalPages: number;
   /** Number of sibling pages to show on each side of current page */
   siblingCount?: number;
   /** Whether the pagination is disabled */
   disabled?: boolean;
}

const props = withDefaults(defineProps<UiPaginationProps>(), {
   siblingCount: 1,
   disabled: false,
});

const emit = defineEmits<{
   pageChange: [page: number];
}>();

/**
 * Generate visible page numbers with ellipsis
 * Pattern: < 1 ... current-1 current current+1 ... last >
 */
const visiblePageNumbers = computed(() => {
   const pages: number[] = [];
   const total = props.totalPages;
   const current = props.currentPage;

   if (total <= 5) {
      // Show all pages if 5 or less
      for (let i = 1; i <= total; i++) {
         pages.push(i);
      }
      return pages;
   }

   // Always show first page
   pages.push(1);

   if (current <= 3) {
      // Near start: 1 2 3 ... last
      pages.push(2);
      pages.push(3);
      pages.push(-1); // ellipsis
      pages.push(total);
   }
   else if (current >= total - 2) {
      // Near end: 1 ... secondLast-1 secondLast last
      pages.push(-1); // ellipsis
      pages.push(total - 2);
      pages.push(total - 1);
      pages.push(total);
   }
   else {
      // Middle: 1 ... current-1 current current+1 ... last
      pages.push(-1); // ellipsis
      pages.push(current - 1);
      pages.push(current);
      pages.push(current + 1);
      pages.push(-1); // ellipsis
      pages.push(total);
   }

   return pages;
});

function goToPage(page: number) {
   if (page >= 1 && page <= props.totalPages && page !== props.currentPage && !props.disabled) {
      emit('pageChange', page);
   }
}

function goToPrevious() {
   if (props.currentPage > 1) {
      goToPage(props.currentPage - 1);
   }
}

function goToNext() {
   if (props.currentPage < props.totalPages) {
      goToPage(props.currentPage + 1);
   }
}

const isFirstPage = computed(() => props.currentPage === 1);
const isLastPage = computed(() => props.currentPage === props.totalPages);
</script>

<template>
   <nav aria-label="Pagination" class="flex items-center justify-center sm:justify-end gap-0.5">
      <!-- Previous button -->
      <button
         type="button"
         class="p-2 hover:bg-neutral rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
         :disabled="isFirstPage || disabled"
         aria-label="Go to previous page"
         @click="goToPrevious"
      >
         <Icon :icon="IconLib.actions.arrow.left" class="size-4" />
      </button>

      <!-- Page numbers -->
      <button
         v-for="(pageNum, index) in visiblePageNumbers"
         :key="`page-${index}`"
         type="button"
         class="px-3 py-1 rounded-lg text-sm font-bold min-w-[40px] flex items-center justify-center transition-colors"
         :class="[
            pageNum === currentPage ? 'bg-neutral-surface' : 'hover:bg-neutral',
            pageNum === -1 ? 'cursor-default' : '',
         ]"
         :disabled="pageNum === -1 || disabled"
         :aria-current="pageNum === currentPage ? 'page' : undefined"
         :aria-label="pageNum === -1 ? 'More pages' : `Go to page ${pageNum}`"
         @click="pageNum !== -1 && goToPage(pageNum)"
      >
         {{ pageNum === -1 ? '...' : pageNum }}
      </button>

      <!-- Next button -->
      <button
         type="button"
         class="p-2 hover:bg-neutral rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
         :disabled="isLastPage || disabled"
         aria-label="Go to next page"
         @click="goToNext"
      >
         <Icon :icon="IconLib.actions.arrow.rightBold" class="size-4" />
      </button>
   </nav>
</template>
